import React, { createContext, useEffect, useContext } from 'react';
import { useTheme as useReduxTheme } from '../components/redux/hooks/useTheme';

/**
 * Theme configuration file
 * This file contains functions to initialize and manage theme settings for Minia theme
 */

// Create a Theme Context
export const ThemeContext = createContext(null);

/**
 * Theme Provider Component
 * Manages theme state and applies theme attributes to the body element.
 * @param {object} { children } - React children to be rendered within the provider's scope.
 */
export const ThemeProvider = ({ children }) => {
  // Get theme from Redux store
  const { theme } = useReduxTheme();

  useEffect(() => {
    // Apply theme settings to the body element
    document.body.setAttribute('data-bs-theme', theme.mode);
    document.body.setAttribute('data-ag-theme-mode', theme.mode); 
    document.body.setAttribute('data-topbar', theme.topbar);
    document.body.setAttribute('data-sidebar', theme.sidebar);

    if (theme.sidebarSize === 'default') {
      document.body.removeAttribute('data-sidebar-size');
    } else if (theme.sidebarSize === 'compact') {
      document.body.setAttribute('data-sidebar-size', 'md');
    } else if (theme.sidebarSize === 'small') {
      document.body.setAttribute('data-sidebar-size', 'sm');
    }

    document.body.setAttribute('data-layout-mode', theme.layoutMode);
    document.body.setAttribute('data-layout-size', theme.layoutWidth);
    document.body.setAttribute('data-layout-scrollable', theme.layoutPosition === 'scrollable' ? 'true' : 'false');
  }, [theme]);

  // Simplified context - components should use Redux hook directly for actions
  const value = {
    theme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Export the ThemeProvider and useTheme hook
export default {
  ThemeProvider,
  useTheme,
};